import { Finance, FinanceChange, FinanceTab } from "./types";

export const financeLesson: { tab: FinanceTab; title: string; text: string; action?: string }[] = [
    { tab: "result", title: "Прочитайте результат", text: "В примере выручка — 600 000 ₽, прочие доходы — 10 000 ₽. Прибыль учитывает фактические расходы, а план помогает оценить безубыточность." },
    { tab: "expenses", title: "Проверьте расходы", text: "Откройте ФОТ и измените фактическую сумму. После сохранения прибыль пересчитается. Процент в плане и фактический расход в рублях — разные поля.", action: "Изменить ФОТ" },
    { tab: "balance", title: "Укажите остаток денег", text: "Подставьте 100 000 ₽ на начало месяца. Подтверждайте полноту расходов только после проверки всех статей, затем сохраните настройки.", action: "Подставить остаток" },
    { tab: "payments", title: "Добавьте будущий платёж", text: "Сохраните заполненный пример закупки материалов. Платёж изменит прогноз денег. Он не добавляет второй расход в расчёт прибыли.", action: "Заполнить платёж" },
    { tab: "result", title: "Сравните прибыль и деньги", text: "Вернитесь к результату: прибыль и остаток денег могут различаться. Расходы влияют на прибыль, а даты платежей — на остаток и возможный кассовый разрыв." },
];

export function monthEnd(month: string): string {
    const [year, number] = month.split("-").map(Number);
    return `${month}-${String(new Date(Date.UTC(year, number, 0)).getUTCDate()).padStart(2, "0")}`;
}

/** Local lesson only. Live reports are always calculated by FinanceService. */
function recalculate(report: Finance): Finance {
    const otherIncome = report.incomes.reduce((sum, item) => sum + item.actual, 0);
    const expenses = report.expenses.reduce((sum, item) => sum + (item.actual ?? 0), 0);
    const fixed = report.expenses.filter(item => item.type === "fixed").reduce((sum, item) => sum + item.value, 0);
    const variableRate = report.expenses.filter(item => item.type === "percent").reduce((sum, item) => sum + item.value, 0);
    const revenue = report.summary.revenue;
    const profit = revenue === null ? null : revenue + otherIncome - expenses;
    const totalIncome = (revenue ?? 0) + otherIncome;
    report.summary = { revenue, otherIncome, expenses, profit,
        margin: revenue !== null && totalIncome > 0 && profit !== null ? Math.sign(profit) * Math.round(Math.abs(profit) / totalIncome * 1000) / 10 : null,
        breakEven: report.expenses.length && variableRate < 100 ? Math.ceil(fixed / (1 - variableRate / 100)) : null, variableRate };
    const days: Finance["cashflow"]["days"] = [];
    let closingBalance = report.balance;
    let firstGap = closingBalance !== null && closingBalance < 0 ? report.balanceOn : null;
    if (closingBalance !== null && report.balanceOn) {
        const end = monthEnd(report.month);
        const flows: Record<string, number> = {};
        report.obligations.forEach(item => {
            if (item.status === "cancelled") return;
            const date = item.status === "paid" ? item.paid_on : item.due_on < report.balanceOn! ? report.balanceOn : item.due_on;
            if (!date || date < report.balanceOn! || date > end) return;
            flows[date] = (flows[date] || 0) + (item.kind === "income" ? 1 : -1) * item.amount;
        });
        for (let day = Number(report.balanceOn.slice(8)); day <= Number(end.slice(8)); day++) {
            const date = `${report.month}-${String(day).padStart(2, "0")}`;
            const flow = flows[date] || 0;
            closingBalance += flow;
            days.push({ date, balance: closingBalance, flow });
            if (closingBalance < 0 && firstGap === null) firstGap = date;
        }
    }
    report.cashflow = { days, firstGap, closingBalance };
    return report;
}

export function createFinanceExample(month: string): Finance {
    return recalculate({ companyId: 0, month, revision: 1, balance: 50000, balanceOn: `${month}-01`, expensesConfirmed: false, revenueSource: "ok",
        summary: { revenue: 600000, otherIncome: 0, expenses: 0, profit: null, margin: null, breakEven: null, variableRate: 0 },
        expenses: [
            { id: 1, name: "ФОТ", type: "percent", value: 40, actual: 240000 },
            { id: 2, name: "Аренда", type: "fixed", value: 60000, actual: 60000 },
            { id: 3, name: "Материалы", type: "percent", value: 5, actual: 30000 },
            { id: 4, name: "Роялти", type: "percent", value: 5, actual: 30000 },
            { id: 5, name: "Коммунальные услуги", type: "fixed", value: 10000, actual: 10000 },
            { id: 6, name: "Маркетинг", type: "fixed", value: 20000, actual: 20000 },
        ],
        incomes: [{ id: 1, name: "Возмещение от поставщика", planned: 10000, actual: 10000 }],
        obligations: [
            { id: 1, title: "Интернет и связь", kind: "expense", amount: 10000, due_on: `${month}-02`, status: "paid", paid_on: `${month}-02`, revision: 1, files: [] },
            { id: 2, title: "Аренда", kind: "expense", amount: 60000, due_on: `${month}-05`, status: "pending", revision: 1, files: [] },
            { id: 3, title: "Ожидаемое поступление", kind: "income", amount: 30000, due_on: `${month}-12`, status: "pending", revision: 1, files: [] },
            { id: 4, title: "Закупка материалов", kind: "expense", amount: 20000, due_on: `${month}-20`, status: "pending", revision: 1, files: [] },
        ], cashflow: { days: [], closingBalance: null, firstGap: null } });
}

export function changeFinanceExample(source: Finance, change: FinanceChange): Finance {
    const next: Finance = { ...source, summary: { ...source.summary }, expenses: source.expenses.map(item => ({ ...item })), incomes: source.incomes.map(item => ({ ...item })), obligations: source.obligations.map(item => ({ ...item, files: [...item.files] })) };
    if (change.action === "settings") {
        next.balance = change.balance;
        next.balanceOn = change.balance === null ? null : change.balanceOn;
        next.expensesConfirmed = change.expensesConfirmed;
    } else if (change.action === "expense") {
        const id = change.id ?? Math.max(0, ...next.expenses.map(item => item.id)) + 1;
        const item = { id, name: change.name, type: change.type, value: change.value, actual: change.actual };
        next.expenses = change.id ? next.expenses.map(row => row.id === id ? item : row) : [...next.expenses, item];
        next.expensesConfirmed = false;
    } else if (change.action === "income") {
        const id = change.id ?? Math.max(0, ...next.incomes.map(item => item.id)) + 1;
        const item = { id, name: change.name, planned: change.planned, actual: change.actual };
        next.incomes = change.id ? next.incomes.map(row => row.id === id ? item : row) : [...next.incomes, item];
        next.expensesConfirmed = false;
    } else if (change.action === "obligation") {
        next.obligations.push({ id: Math.max(0, ...next.obligations.map(item => item.id)) + 1, title: change.title, kind: change.kind, amount: change.amount, due_on: change.dueOn, status: "pending", revision: 1, files: [] });
    } else {
        next.obligations = next.obligations.map(item => item.id === change.id ? { ...item, status: change.status, paid_on: change.status === "paid" ? change.paidOn ?? null : null, revision: item.revision + 1 } : item);
    }
    next.revision++;
    return recalculate(next);
}
